import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useSC, Kpi, Card, money, num, monthLabel, Loading, Pill, SCEN_TONE } from "./parts";

const COLORS = ["#14142B", "#B93B3B", "#F5E11B", "#2C633D", "#2F4F8F", "#8B6D00", "#4A4A8A"];

function TargetBar({ r }) {
  return (
    <Card title="Sales target this financial year" right={<span className="text-xs font-bold" style={{ color: "#2C633D" }}>▲ {r.pct}% achieved</span>}>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="font-display text-4xl" style={{ color: "var(--primary)" }} data-testid="sc-achieved">{money(r.achieved)}</div>
        <div className="text-sm pb-1" style={{ color: "var(--text-secondary)" }}>of {money(r.target)} target · forecast {money(r.forecast)}</div>
      </div>
      <div className="h-3 rounded-full mt-3 overflow-hidden" style={{ background: "var(--bg)" }}>
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.min(100, r.pct)}%`, background: "linear-gradient(90deg,#14142B,#B93B3B)" }} />
      </div>
    </Card>
  );
}

export function OverviewTab({ onOpenDietitian }) {
  const { data: d } = useSC("/overview");
  if (!d) return <Loading />;
  const t = d.totals, r = d.revenue;
  const lastFull = r.monthly.filter((m) => m.growth_pct !== null).slice(-1)[0];
  const mtd = r.monthly.find((m) => m.partial);
  const chart = r.monthly.filter((m) => !m.partial);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="sc-kpi-registered" label="Registered clients" value={num(t.registered)} sub={`${num(t.running)} active / running`} />
        <Kpi label="Team" value={`${t.dietitians} + ${t.coaches}`} sub="dietitians + yoga coaches" />
        <Kpi label="Yoga" value={`${t.batches} batches`} sub={`${t.classes_per_week} classes/week · ${num(t.yoga_members)} members`} />
        <Kpi label="Renewal rate" value={`${d.retention_pct}%`} sub={`${num(t.plans_delivered)} diet plans delivered`} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2"><TargetBar r={r} /></div>
        <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
          <Kpi label="New-customer revenue" value={money(r.new)} accent="var(--red)" />
          <Kpi label="Renewal revenue" value={money(r.renewal)} sub={lastFull ? `Last month growth +${lastFull.growth_pct}%` : ""} accent="var(--red)" />
        </div>
      </div>
      <Card title={`Monthly sales · ${d.fy_label}`} right={mtd && <span className="text-xs font-bold" style={{ color: "var(--text-secondary)" }}>{monthLabel(mtd.month)} so far: {money(mtd.revenue)}</span>}>
        <div className="h-64"><ResponsiveContainer><AreaChart data={chart}>
          <defs><linearGradient id="scg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B93B3B" stopOpacity={0.35} /><stop offset="100%" stopColor="#B93B3B" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(20,20,43,0.08)" /><XAxis dataKey="month" tickFormatter={monthLabel} fontSize={11} />
          <YAxis fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} /><Tooltip formatter={(v) => money(v)} labelFormatter={monthLabel} />
          <Area dataKey="revenue" stroke="#B93B3B" strokeWidth={2.5} fill="url(#scg)" />
        </AreaChart></ResponsiveContainer></div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Dietitian-wise revenue (this FY)">
          <div className="h-60"><ResponsiveContainer><BarChart data={d.dietitians} layout="vertical" margin={{ left: 20 }}>
            <XAxis type="number" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} /><YAxis type="category" dataKey="name" width={110} fontSize={11} />
            <Tooltip formatter={(v) => money(v)} /><Bar dataKey="revenue_fy" fill="#14142B" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(x) => onOpenDietitian(x.id)} />
          </BarChart></ResponsiveContainer></div>
        </Card>
        <Card title="Subscription revenue by package">
          <div className="h-60"><ResponsiveContainer><PieChart>
            <Pie data={r.by_package} dataKey="revenue" nameKey="name" innerRadius={50} outerRadius={85}>{r.by_package.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie>
            <Tooltip formatter={(v) => money(v)} />
          </PieChart></ResponsiveContainer></div>
        </Card>
      </div>
      <Card title="Client distribution">
        <div className="flex flex-wrap gap-2">{d.distribution.map((s) => <span key={s.key} className="flex items-center gap-2 text-sm"><Pill tone={SCEN_TONE[s.key]}>{s.label}</Pill><b>{num(s.count)}</b></span>)}</div>
      </Card>
    </div>
  );
}
